/**
 * useUserDetail Hook
 * 
 * Custom hook for managing user detail data and payment history.
 */

import * as React from "react";
import { paymentService, type HistoricalTransaction } from "@/services/paymentService";
import { launchpadPaymentService, type LaunchpadPaymentStatus } from "@/services/launchpadPaymentService";

interface UseUserDetailProps {
  userId?: string;
  callsign?: string;
  activeTab: string;
}

export function useUserDetail({ userId, callsign, activeTab }: UseUserDetailProps) {
  const [payments, setPayments] = React.useState<HistoricalTransaction[]>([]);
  const [paymentsLoading, setPaymentsLoading] = React.useState(false);
  const [paymentsError, setPaymentsError] = React.useState<string | null>(null);
  const [launchpadStatus, setLaunchpadStatus] = React.useState<LaunchpadPaymentStatus | null>(null);
  const [launchpadLoading, setLaunchpadLoading] = React.useState(false);

  // Fetch payment history and launchpad status when pembayaran tab is active
  React.useEffect(() => {
    if (activeTab === "pembayaran" && userId) {
      const fetchPayments = async () => {
        setPaymentsLoading(true);
        setPaymentsError(null);
        try {
          const result = await paymentService.fetchHistoricalTransactions({
            idClient: userId,
            limit: 20,
          });
          setPayments(result.data);
        } catch (err) {
          console.error("Error fetching payment history:", err);
          setPaymentsError(err instanceof Error ? err.message : "Gagal memuat riwayat pembayaran");
          setPayments([]);
        } finally {
          setPaymentsLoading(false);
        }
      };

      fetchPayments();
    }
  }, [activeTab, userId]);

  // Check launchpad payment status when pembayaran tab is active
  React.useEffect(() => {
    if (activeTab === "pembayaran" && callsign) {
      const fetchLaunchpadStatus = async () => {
        setLaunchpadLoading(true);
        try {
          const status = await launchpadPaymentService.checkStatusLaunchpad(callsign);
          setLaunchpadStatus(status);
        } catch (err) {
          console.error("Error checking launchpad payment status:", err);
          setLaunchpadStatus(null);
        } finally {
          setLaunchpadLoading(false);
        }
      };

      fetchLaunchpadStatus();
    }
  }, [activeTab, callsign]);

  return {
    payments,
    paymentsLoading,
    paymentsError,
    launchpadStatus,
    launchpadLoading,
  };
}

